
import { create } from 'zustand';
import apiClient from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { getImageUrl, extractImageUrls } from '@/config/media';

// Types matching mobile app
export interface Vacancy {
  id: string;
  title: string;
  location: string;
  price: number;          // mapped from rent_amount
  price_unit: string;
  images: string[];       // mapped from image_urls
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  type?: string;
  landlord_id: string;
  is_active: boolean;
  created_at: string;
}

export interface Product {
  id: string;
  business_id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  quantity_in_stock: number;
  rating: number;
  media_urls: any;
  image_url?: string;
  business_name: string;
  is_featured: boolean;
  is_active: boolean;
  created_at?: string;
}

export interface Service {
  id: string;
  business_id: string;
  name: string;
  description: string;
  category: string;
  price: number;
  duration: number;
  rating: number;
  media_urls: any;
  business_name: string;
  delivery_mode: string;
  is_featured: boolean;
  created_at?: string;
}

interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
}

interface HomeState {
  // Data
  featuredVacancies: Vacancy[];
  featuredProducts: Product[];
  featuredServices: Service[];

  // Loading states
  loading: boolean;
  vacanciesLoading: boolean;
  productsLoading: boolean;
  servicesLoading: boolean;

  // Error states
  error: string | null;
  vacanciesError: string | null;
  productsError: string | null;
  servicesError: string | null;

  // Actions
  fetchHomeData: () => Promise<void>;
  fetchFeaturedVacancies: () => Promise<void>;
  fetchFeaturedProducts: () => Promise<void>;
  fetchFeaturedServices: () => Promise<void>;
  clearErrors: () => void;
}

export const useHomeStore = create<HomeState>((set, get) => ({
  // Initial state
  featuredVacancies: [],
  featuredProducts: [],
  featuredServices: [],
  loading: false,
  vacanciesLoading: false,
  productsLoading: false,
  servicesLoading: false,
  error: null,
  vacanciesError: null,
  productsError: null,
  servicesError: null,

  fetchHomeData: async () => {
    const state = get();
    if (state.loading) return;

    set({ loading: true, error: null });

    try {
      await Promise.allSettled([
        get().fetchFeaturedVacancies(),
        get().fetchFeaturedProducts(),
        get().fetchFeaturedServices(),
      ]);
    } catch (error) {
      console.error('Error fetching home data:', error);
      set({ error: 'Failed to load home data' });
    } finally {
      set({ loading: false });
    }
  },

  fetchFeaturedVacancies: async () => {
    set({ vacanciesLoading: true, vacanciesError: null });

    try {
      console.log('[HomeStore] Fetching vacancies from backend...');

      const response = await apiClient.get<PaginatedResponse<Vacancy>>(
        API_ENDPOINTS.VACANCIES,
        {
          params: {
            limit: 10,
            sortBy: 'newest',
            is_active: true
          },
        }
      );

      // Handle different response structures
      const rawItems: any[] = response.data?.items || response.data || [];

      const vacanciesData = rawItems.map((v: any) => ({
        id: v.id,
        title: v.title || v.description || 'Untitled',
        location: v.property?.location_address || v.unit?.address || '',
        price: v.rent_amount || v.price || 0,
        price_unit: v.price_unit || 'month',
        images: Array.isArray(v.image_urls)
          ? v.image_urls.map(getImageUrl).filter(Boolean)
          : extractImageUrls(v.image_urls),
        bedrooms: v.unit?.bedrooms ?? v.bedrooms,
        bathrooms: v.unit?.bathrooms ?? v.bathrooms,
        area: v.unit?.area ?? v.area,
        type: v.unit?.unit_type || v.type || 'apartment',
        landlord_id: v.landlord_id,
        is_active: v.status === 'active' || v.is_active || true,
        created_at: v.created_at,
      }));

      console.log('[HomeStore] Vacancies fetched:', vacanciesData.length);

      set({
        featuredVacancies: vacanciesData,
        vacanciesLoading: false,
      });
    } catch (error: any) {
      console.error('[HomeStore] Failed to fetch featured vacancies:', error);

      const errorMessage = 'Vacancies temporarily unavailable';

      set({
        vacanciesLoading: false,
        vacanciesError: errorMessage,
        featuredVacancies: [],
      });
    }
  },

  fetchFeaturedProducts: async () => {
    set({ productsLoading: true, productsError: null });

    try {
      console.log('[HomeStore] Fetching products from backend...');

      const response = await apiClient.get(API_ENDPOINTS.PRODUCTS, {
        params: {
          limit: '10',
          sort: 'created_at:desc',
          is_active: true
        },
      }).catch(error => {
        console.warn('[HomeStore] Products API error, using fallback:', error);
        return { data: { items: [] } }; // Return empty array structure
      });

      // Handle different response structures SAFELY
      let productsData: any[] = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          productsData = response.data;
        } else if (response.data.products && Array.isArray(response.data.products)) {
          productsData = response.data.products;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          productsData = response.data.items;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          productsData = response.data.data;
        } else {
          const possibleArrays = Object.values(response.data).find(val => Array.isArray(val));
          if (possibleArrays) productsData = possibleArrays as any[];
        }
      }

      console.log('[HomeStore] Products data structure:', {
        data: response.data,
        extracted: productsData
      });

      const featuredProducts = productsData.map((product: any) => ({
        id: product.id || product._id || `product-${Date.now()}`,
        business_id: product.business_id || product.business?._id || '',
        name: product.name || product.title || 'Unnamed Product',
        description: product.description || '',
        category: product.category || 'Uncategorized',
        price: product.price || 0,
        quantity_in_stock: product.quantity_in_stock || product.stock_quantity || 0,
        rating: product.rating || 0,
        media_urls: product.media_urls || product.images || product.image_urls || [],
        image_url: product.image_url || product.image || '',
        business_name: product.business_name || product.business?.name || 'Unknown Business',
        is_featured: product.is_featured || false,
        is_active: product.is_active !== undefined ? product.is_active : true,
        created_at: product.created_at || new Date().toISOString(),
        _safeImageUrls: extractImageUrls(product.media_urls || product.images || product.image_urls),
      }));

      console.log('[HomeStore] Products fetched:', featuredProducts.length);

      set({ featuredProducts, productsLoading: false });
    } catch (error: any) {
      console.error('[HomeStore] Error in fetchFeaturedProducts:', error);

      // Fallback to empty array
      set({
        productsLoading: false,
        productsError: 'Products temporarily unavailable',
        featuredProducts: [],
      });
    }
  },

  fetchFeaturedServices: async () => {
    set({ servicesLoading: true, servicesError: null });

    try {
      console.log('[HomeStore] Fetching services from backend...');

      const response = await apiClient.get(API_ENDPOINTS.SERVICES, {
        params: {
          limit: '10',
          sort: 'created_at:desc',
          is_active: true
        },
      }).catch(error => {
        console.warn('[HomeStore] Services API error, using fallback:', error);
        return { data: { items: [] } };
      });

      // Handle different response structures SAFELY
      let servicesData: any[] = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (response.data.services && Array.isArray(response.data.services)) {
          servicesData = response.data.services;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          servicesData = response.data.items;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          servicesData = response.data.data;
        } else {
          const possibleArrays = Object.values(response.data).find(val => Array.isArray(val));
          if (possibleArrays) servicesData = possibleArrays as any[];
        }
      }

      console.log('[HomeStore] Services data structure:', {
        data: response.data,
        extracted: servicesData
      });

      const featuredServices = servicesData.map((service: any) => ({
        id: service.id || service._id || `service-${Date.now()}`,
        business_id: service.business_id || '',
        name: service.name || service.title || 'Unnamed Service',
        description: service.description || '',
        category: service.category || 'Uncategorized',
        price: service.price || 0,
        duration: service.duration_minutes || service.duration || 60,
        rating: service.rating || 0,
        media_urls: service.media_urls || [],
        business_name: service.business_name || 'Unknown Business',
        delivery_mode: service.delivery_mode || 'onsite',
        is_featured: service.is_featured || false,
        created_at: service.created_at || new Date().toISOString(),
        _safeImageUrls: extractImageUrls(service.media_urls),
      }));

      console.log('[HomeStore] Services fetched:', featuredServices.length);

      set({ featuredServices, servicesLoading: false });
    } catch (error: any) {
      console.error('[HomeStore] Error in fetchFeaturedServices:', error);

      set({
        servicesLoading: false,
        servicesError: 'Services temporarily unavailable',
        featuredServices: [],
      });
    }
  },

  clearErrors: () => {
    set({
      error: null,
      vacanciesError: null,
      productsError: null,
      servicesError: null,
    });
  },
}));